import moment from "moment";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "react-query";
import { findCompaniesV3 } from "../../../../repositories/qerpapi/v3/EmpresaRepository";
import { notify } from "../../../../utils/notify";

import { findPayableAccounts } from "../../../../repositories/qerpapi/erp/relatorio/ContasPagarRepository";
import { findPayableSituations } from "../../../../repositories/qerpapi/v3/SituacoesRepository";
import { periods } from "../../constants";
import {
  accountsPayableColumns,
  groupBy,
  payableInitialFilters,
  situations,
} from "../ContasPagarConstants";

const ContasPagarContext = createContext();

const ContasPagarProvider = ({ children }) => {
  const [filters, setFilters] = useState({ ...payableInitialFilters });

  const [filterPeriod, setFilterPeriod] = useState({
    ...payableInitialFilters.searchDate,
  });

  const [isMainFetchEnabled, setMainFetchEnabled] = useState(false);
  const [isGrouped, setIsGrouped] = useState(false);
  const [isDrawerOpen, setDrawerOpen] = useState(false);

  const [columns, setColumns] = useState(accountsPayableColumns);

  const [cachedCompanies, setCachedCompanies] = useState({});
  const [cachedSituations, setCachedSituations] = useState([]);

  const [cachedMovementData, setCachedMovementData] = useState([]);
  const [cachedPeriodTotal, setCachedPeriodTotal] = useState();
  const [cachedPeriod, setCachedPeriod] = useState({});

  const [quickSituations, setQuickSituations] = useState(situations);

  const [isIndeterminate, setIndeterminate] = useState(false);

  // ----------------------------------------------------------------------------

  const setSelectedCompany = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      multicompany: newValue,
    }));
  }, []);

  const setCustomDatePeriod = useCallback((newValue) => {
    setFilterPeriod((prev) => {
      return {
        ...prev,
        ...newValue,
      };
    });
  }, []);

  const setPeriod = useCallback(
    (newValue) => {
      if (newValue === periods.Personalizado) {
        setCustomDatePeriod({
          startDate: undefined,
          endDate: undefined,
        });
      }

      setFilters((prev) => ({
        ...prev,
        period: newValue,
      }));
    },
    [setCustomDatePeriod]
  );

  const setSearchDate = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      startDate: newValue?.startDate
        ? moment(newValue?.startDate).format("YYYY-MM-DD")
        : moment().startOf("month").format("YYYY-MM-DD"),
      endDate: newValue?.endDate
        ? moment(newValue?.endDate).format("YYYY-MM-DD")
        : moment().endOf("month").format("YYYY-MM-DD"),
    }));
  }, []);

  const setParticipant = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, participants: newValue }));
  }, []);

  const setName = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, descriptionParticipants: newValue }));
  }, []);

  const setStatus = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, status: newValue }));
  }, []);

  const setDateFilter = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, dateFilter: newValue }));
  }, []);

  const setGroupBy = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, group: newValue }));
  }, []);

  const setSortBy = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, sort: newValue }));
  }, []);

  const setSortDirection = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, sortDirection: newValue }));
  }, []);

  const setSituations = useCallback(() => {
    const checkedSituations = cachedSituations
      .filter((situation) => situation.checked)
      .map((situation) => situation.id);

    const checkedQuickSituations = quickSituations
      .filter((quickSituation) => quickSituation.checked)
      .map((quickSituation) => quickSituation.id);

    setFilters((prev) => ({
      ...prev,
      status: checkedSituations,
      quickStatus: checkedQuickSituations,
    }));
  }, [cachedSituations, quickSituations]);

  // ----------------------------------------------------------------------------

  const applyPeriod = useCallback(
    (periodDates) => {
      setSearchDate(periodDates);
      setCachedPeriod(periodDates);
      setCustomDatePeriod({
        startDate: periodDates?.startDate
          ? moment(periodDates.startDate, "YYYY-MM-DD").toDate()
          : moment().startOf("month").toDate(),
        endDate: periodDates?.endDate
          ? moment(periodDates.endDate, "YYYY-MM-DD").toDate()
          : moment().endOf("month").toDate(),
      });
    },
    [setCustomDatePeriod, setSearchDate]
  );

  useEffect(() => {
    setSearchDate(filterPeriod);
  }, [filterPeriod, setSearchDate]);

  // ----------------------------------------------------------------------------

  const {
    isLoading: mainLoading,
    refetch,
    isRefetching: mainRefetchLoading,
  } = useQuery("contas-pagar", async () => await findPayableAccounts(filters), {
    enabled: !!sessionStorage.getItem("gfAccessToken") && isMainFetchEnabled,
    onSuccess: async (res) => await handleMainData(res),
  });

  const handleMainData = useCallback(
    async (res) => {
      const data = res?.data;

      if (!res?.success) {
        if (res.statusCode === 401) {
          notify("Token expirado", true, "error");
        }
        notify(res.message, true, "error");
      }
      applyPeriod(data?.searchPeriod);
      setCachedMovementData(data?.movements || []);
      setCachedPeriodTotal(data?.total);
      setIsGrouped(filters.group !== groupBy["Sem agrupamento"]);
    },
    [applyPeriod, filters.group]
  );

  // ----------------------------------------------------------------------------

  const { isLoading: loadingCompanies } = useQuery(
    "companies",
    async () => await findCompaniesV3(),
    {
      enabled: !!sessionStorage.getItem("gfAccessToken"),
      onSuccess: (companies) => handleCompaniesData(companies),
    }
  );

  const validateIfCompanyIdExistAndSetNewDefault = useCallback(
    async (userCompanies) => {
      const companiesIds = Object.values(userCompanies);

      if (!companiesIds.includes(filters.multicompany)) {
        setSelectedCompany(userCompanies.Todos);
      }
    },
    [filters.multicompany, setSelectedCompany]
  );

  const handleCompaniesData = useCallback(
    async (companies) => {
      const data = companies?.data ?? [];

      if (!companies.success) notify(companies.message, true, "error");

      if (companies?.data) {
        const allCompaniesString = data.map((company) => company.id).join(",");
        const formatedCompanies = {
          Todos: allCompaniesString,
        };

        data.forEach(
          (company) => (formatedCompanies[company.nomeempresa] = company.id)
        );

        await validateIfCompanyIdExistAndSetNewDefault(formatedCompanies);

        setCachedCompanies(formatedCompanies);
        setMainFetchEnabled(true);
      }
    },
    [validateIfCompanyIdExistAndSetNewDefault]
  );

  // ----------------------------------------------------------------------------

  const { isLoading: isSituationsLoading } = useQuery(
    "situations",
    async () =>
      await findPayableSituations(sessionStorage.getItem("empresa_cnpj")),
    {
      enabled:
        !!sessionStorage.getItem("gfAccessToken") &&
        !!sessionStorage.getItem("empresa_cnpj"),
      onSuccess: (situations) => handleSituationsData(situations),
    }
  );

  const handleSituationsData = useCallback(async (situations) => {
    const data = situations?.data ?? [];

    if (!situations.success) notify(situations.message, true, "error");

    if (data.length) {
      const formattedSituations = data
        .sort((situationA, situationB) =>
          situationA.descricao < situationB.descricao ? -1 : 1
        )
        .map((dataItem) => {
          return {
            id: dataItem.codigo,
            desc: dataItem.descricao,
            checked: false,
          };
        });

      setCachedSituations(formattedSituations);
    }
  }, []);

  // ----------------------------------------------------------------------------

  const handleCheckAll = useCallback(
    (check) => {
      const checekdSituations = cachedSituations.map((situation) => {
        situation.checked = check;
        return situation;
      });
      setIndeterminate(
        checekdSituations.some((situation) => situation.checked)
      );
      setCachedSituations(checekdSituations);
    },
    [cachedSituations]
  );

  const handleCheckSituations = useCallback(
    (checkedSituation, check) => {
      const checekdSituations = cachedSituations.map((situation) => {
        if (checkedSituation.id === situation.id) {
          situation.checked = check;
        }
        return situation;
      });

      const isAllSituationsCheckeds = checekdSituations.every(
        (checkedSituation) => checkedSituation.checked
      );
      const isSomeSituationsCheckeds = checekdSituations.some(
        (checkedSituation) => checkedSituation.checked
      );

      setCachedSituations(checekdSituations);

      if (isAllSituationsCheckeds) {
        handleCheckAll(check);
        return;
      }

      setIndeterminate(isSomeSituationsCheckeds);
    },
    [cachedSituations, handleCheckAll]
  );

  const handleCheckQuickSituations = useCallback(
    (checkedSituation, check) => {
      const checkedSituations = quickSituations.map((situation) => {
        if (checkedSituation.id === situation.id) {
          situation.checked = check;
        }
        return situation;
      });

      setQuickSituations(checkedSituations);
    },
    [quickSituations]
  );

  const handleRemoveChip = useCallback(
    (checkedSituation) => {
      const isQuickSituation = quickSituations.some(
        (quick) => quick.id === checkedSituation.id
      );
      const isSituation = cachedSituations.some(
        (situation) => situation.id === checkedSituation.id
      );

      if (isQuickSituation) {
        handleCheckQuickSituations(checkedSituation, false);
      }

      if (isSituation) {
        handleCheckSituations(checkedSituation, false);
      }

      setSituations();
    },
    [
      cachedSituations,
      handleCheckQuickSituations,
      handleCheckSituations,
      quickSituations,
      setSituations,
    ]
  );

  const clearAllSituationsFilters = useCallback(() => {
    handleCheckAll(false);
    const checkedSituations = quickSituations.map((situation) => {
      situation.checked = false;

      return situation;
    });

    setQuickSituations(checkedSituations);
    setQuickSituations(checkedSituations);
  }, [handleCheckAll, quickSituations]);

  // ----------------------------------------------------------------------------

  const contextValue = useMemo(
    () => ({
      filters,
      setFilters,
      filterPeriod,
      setFilterPeriod,
      setCachedSituations,

      columns,
      setColumns,

      isGrouped,

      isDrawerOpen,
      setDrawerOpen,

      mainLoading,
      refetch,
      mainRefetchLoading,

      cachedMovementData,
      cachedPeriod,
      cachedPeriodTotal,

      cachedSituations,
      isSituationsLoading,

      quickSituations,
      setQuickSituations,
      isIndeterminate,
      setIndeterminate,

      handleCheckQuickSituations,
      handleCheckSituations,
      handleCheckAll,
      handleRemoveChip,
      clearAllSituationsFilters,

      cachedCompanies,
      loadingCompanies,

      setSelectedCompany,
      setPeriod,
      setSearchDate,
      setCustomDatePeriod,
      setParticipant,
      setName,
      setStatus,
      setDateFilter,
      setGroupBy,
      setSortBy,
      setSortDirection,
      setSituations,
    }),
    [
      filters,
      filterPeriod,
      columns,
      isGrouped,
      isDrawerOpen,
      mainLoading,
      refetch,
      mainRefetchLoading,
      cachedMovementData,
      cachedPeriod,
      cachedPeriodTotal,
      cachedSituations,
      isSituationsLoading,
      quickSituations,
      isIndeterminate,
      handleCheckQuickSituations,
      handleCheckSituations,
      handleCheckAll,
      handleRemoveChip,
      clearAllSituationsFilters,
      cachedCompanies,
      loadingCompanies,
      setSelectedCompany,
      setPeriod,
      setSearchDate,
      setCustomDatePeriod,
      setParticipant,
      setName,
      setStatus,
      setDateFilter,
      setGroupBy,
      setSortBy,
      setSortDirection,
      setSituations,
    ]
  );

  return (
    <ContasPagarContext.Provider value={contextValue}>
      {children}
    </ContasPagarContext.Provider>
  );
};

const useContasPagarService = () => {
  const context = useContext(ContasPagarContext);

  if (!context) {
    throw new Error(
      "useContasPagarService must be used with a ContasPagarContextProvider"
    );
  }

  return context;
};

export { ContasPagarProvider, useContasPagarService };
